import {BannerTransport} from '../transport/BannerTransport';
import {getCompactFooterHeight, getEffectiveViewportHeight, isDarkThemeSelected} from '../util/BannerCommon';
import {BannerConstant} from '../util/BannerConstant';

import {BannerCfg} from './BannerCfg';

interface RbSyncCfg {
    id: string;
}

declare global {
    interface Window {
        rb_sync?: RbSyncCfg;
    }
}

let PERSONAL_DATA_PARAMETER_BY_KEY: Record<string, string> = {};

PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstant.PERSONAL_DATA_ID_KEY] = "user_id";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstant.PERSONAL_DATA_NAME_KEY] = "user_name";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstant.PERSONAL_DATA_DAYS_TO_BIRTH_KEY] = "days_to_birth";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstant.PERSONAL_DATA_BIRTH_DAY_KEY] = "days_birth";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstant.PERSONAL_DATA_BIRTH_MONTH_KEY] = "month_birth";

export class BannerParams {
    private bannerCfg: BannerCfg | undefined;
    private withUniqueId: boolean;
    private withPersonalData: boolean;
    private withLastApps: boolean;
    private withViewportHeight = false;
    private considerCompactFooter = false;
    private withDarkThemeParam = false;
    private withFpidParam = false;

    constructor(withUniqueId: boolean, withPersonalData: boolean, withLastApps: boolean) {
        this.withUniqueId = withUniqueId;
        this.withPersonalData = withPersonalData;
        this.withLastApps = withLastApps;
    }

    asJSON(): Record<string, string> {
        const params: Record<string, string> = {};
        const {bannerTargeting, experiment, language, lastApplications} = this.getBannerCfg();

        const t = bannerTargeting[BannerConstant.TARGETING_PARAMETER];
        if (t) {
            BannerTransport.appendParam(params, BannerConstant.TARGETING_PARAMETER, t);
        } else {
            const age = bannerTargeting[BannerConstant.USER_AGE];
            if (age) {
                BannerTransport.appendParam(params, BannerConstant.USER_AGE, age);
                BannerTransport.appendParam(params, BannerConstant.USER_GENDER, bannerTargeting[BannerConstant.USER_GENDER]);
            }
        }

        const uniqueId = bannerTargeting[BannerConstant.UNIQUE_ID_KEY];
        if (this.withUniqueId && uniqueId !== null) {
            BannerTransport.appendParam(params, BannerConstant.UNIQUE_ID_KEY, uniqueId);
        }

        const personalData = BannerParams.getPersonalData(bannerTargeting);
        if (this.withPersonalData && personalData !== null) {
            BannerTransport.appendParam(params, BannerConstant.PERSONAL_DATA_KEY, personalData);
        }

        if (language) {
            /* Добавляем параметр выбранного языка */
            BannerTransport.appendParam(params, BannerConstant.LANGUAGE, language);
        }

        if (this.withLastApps && lastApplications) {
            params[BannerConstant.WITH_LAST_APPS] = lastApplications;
        }

        if (this.withViewportHeight) {
            let height = getEffectiveViewportHeight();
            if (this.considerCompactFooter) {
                height -= getCompactFooterHeight();
            }
            params[BannerConstant.VIEWPORT_HEIGHT] = height + '';
        }

        if (experiment) {
            params[BannerConstant.EXPR] = experiment;
        }
        if (this.isWithDarkThemeParam() && isDarkThemeSelected()) {
            params[BannerConstant.IS_DARK_THEME_PARAM_NAME] = '1';
        }
        if (this.isWithFpidParam()) {
            const fpid = this.getFpid();
            if (fpid) {
                params[BannerConstant.FPID_PARAM] = fpid;
            }
        }
        return params;
    }

    private getFpid() {
        return window.rb_sync?.id || null;
    }

    getBannerCfg(): BannerCfg {
        if (!this.bannerCfg) {
            this.bannerCfg = new BannerCfg();
        }
        return this.bannerCfg;
    }

    setBannerCfg(bannerCfg: BannerCfg) {
        this.bannerCfg = bannerCfg;
    }

    setWithViewportHeight(withViewportHeight: boolean) {
        this.withViewportHeight = withViewportHeight;
    }

    setConsiderCompactFooter() {
        this.considerCompactFooter = true;
    }

    isCallbacksEnabled(): boolean {
        return this.getBannerCfg().callbacksEnabled;
    }

    isWithDarkThemeParam(): boolean {
        return this.withDarkThemeParam;
    }

    setWithDarkThemeParam(withDarkThemeParam: boolean) {
        this.withDarkThemeParam = withDarkThemeParam;
    }

    isWithFpidParam(): boolean {
        return this.withFpidParam;
    }

    setWithFpidParam(withFpidParam: boolean) {
        this.withFpidParam = withFpidParam;
    }

    static getPersonalData(json: Record<string, unknown>): (string|null) {
        const personalData = json[BannerConstant.PERSONAL_DATA_KEY] as Record<string, string | number>;
        if (!personalData) {
            return null;
        }

        let buf: string[] = [];

        Object.getOwnPropertyNames(personalData).forEach((key: string) => {
            buf.push(`${PERSONAL_DATA_PARAMETER_BY_KEY[key]}=${personalData[key]}`);
        });

        return buf.join('&');
    }
}
